"use client";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MarketplaceAccountTrigger } from "./marketplace-account-trigger";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/use-current-user";
import { ThemeToggle } from "@/components/common/theme-toggle";
import { MenuIcon, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import dynamic from "next/dynamic";

const MobileMenuContent = dynamic(
  () =>
    import("./mobile-menu-content").then((module) => module.MobileMenuContent),
  {
    loading: () => (
      <output className="px-5 py-4 text-sm text-muted-foreground">
        Loading menu…
      </output>
    ),
  },
);

export function MobileMenu() {
  const { user, displayAuthDialog } = useCurrentUser();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const authRequestedRef = useRef(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  function closeMenu() {
    setOpen(false);
  }

  function openLogin() {
    authRequestedRef.current = true;
    closeMenu();
    displayAuthDialog();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!user && (
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            className="h-10 w-10 p-0"
            aria-label="Open menu"
          >
            <MenuIcon />
          </Button>
        </DialogTrigger>
      )}

      {user && (
        <DialogTrigger asChild>
          <MarketplaceAccountTrigger compact />
        </DialogTrigger>
      )}

      <DialogContent
        disableAnimation
        displayCloseButton={false}
        overlayClassName="z-[1199] bg-black/30"
        onCloseAutoFocus={(event) => {
          if (!authRequestedRef.current) return;
          // Let the auth dialog own focus instead of returning to the menu trigger.
          event.preventDefault();
          authRequestedRef.current = false;
        }}
        className="!left-auto !right-0 !top-0 !z-[1201] !translate-x-0 !translate-y-0 !h-dvh !max-h-dvh !w-[85vw] !max-w-[400px] !gap-0 !overflow-hidden !border-l !border-border !bg-background !p-0 !text-foreground !shadow-[0_10px_35px_rgba(0,0,0,0.12)] data-[state=closed]:translate-x-full data-[state=open]:translate-x-0 transition-transform duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] dark:!shadow-[0_10px_35px_rgba(0,0,0,0.45)]"
      >
        <DialogTitle className="sr-only">Navigation menu</DialogTitle>
        <div className="flex h-full min-h-0 flex-col">
          <div className="sticky top-0 z-10 flex items-center justify-between bg-background px-5 pb-2 pt-4">
            <DialogClose asChild>
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </button>
            </DialogClose>
            <ThemeToggle />
          </div>

          {open && (
            <MobileMenuContent closeMenu={closeMenu} openLogin={openLogin} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

MobileMenu.displayName = "MobileMenu";
